import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import {} from 'react';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING_WITHIN } from '../../lib/focus-ring.js';
import { RichText } from '../../atoms/RichText/RichText.js';
/** A–D, and onwards. The cap of six options is enforced by the parser. */
export const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
/**
 * Question option (handoff, Molecules: "the most-used molecule").
 *
 * A `<label>` wrapping a visually hidden radio, which is the handoff's first
 * suggestion and the one that costs nothing: a native radio group already
 * gives arrow-key movement, a roving tab stop, and the right announcement,
 * none of which a div with `role="radio"` gets for free. What is left to write
 * by hand is the letter keys, and those belong to the question rather than to
 * an option: see `handleOptionLetterKey`.
 *
 * Focus goes with the selection, however the option was chosen. Arrow keys and
 * a click already do that in most browsers; Safari does not focus a radio that
 * is clicked, and a letter key is pressed while focus is somewhere else, so the
 * option takes focus itself when it becomes the selected one. A student never
 * sees one option focused and another selected.
 *
 * **No correctness cue, ever.** There is no `correct` prop here, and there is
 * nothing for one to render. The student-facing GraphQL type has no such field
 * either (V1.md section 5), so this is the second lock rather than the first.
 */
export function QuestionOption({ name, value, letter, children, checked, defaultChecked, onSelect, readOnly = false, className, }) {
    return (_jsxs("label", { "data-option-letter": letter, className: cn('group relative flex min-h-touch-target-min cursor-pointer items-center gap-4 rounded-option', 'border-2 border-border bg-surface px-5 py-2.5', 'transition-colors', readOnly ? 'cursor-default' : 'hover:border-text-disabled hover:bg-background', 'has-[:checked]:border-primary has-[:checked]:bg-primary-light', 
        // The shared ring, on the card rather than on the hidden radio inside
        // it. It was a 35% glow of its own, which was the one focus ring in
        // the design system that did not match the others, too faint to read
        // against white, and dropped entirely in forced-colours mode. The
        // selected state is the border and the tint; focus is the ring
        // around them, so a card can be either, or both, and say which.
        FOCUS_RING_WITHIN, className), children: [_jsx("input", { type: "radio", name: name, value: value, className: "sr-only", disabled: readOnly, "aria-keyshortcuts": readOnly ? undefined : letter, ...(checked === undefined ? { defaultChecked } : { checked }), onChange: (event) => {
                    const input = event.currentTarget;
                    if (document.activeElement !== input)
                        input.focus();
                    onSelect?.(value);
                } }), _jsx("span", { "aria-hidden": "true", className: cn('flex size-8 shrink-0 items-center justify-center rounded-full border-2', 'border-text-disabled transition-colors', 'group-hover:border-text-secondary', 'group-has-[:checked]:border-primary group-has-[:checked]:bg-primary', 
                // The dot is a fill inside a fill, and forced colours repaints both
                // to the page colour: selected looked exactly like unselected. The
                // system highlight marks it there instead.
                'forced-colors:group-has-[:checked]:border-[Highlight] forced-colors:group-has-[:checked]:bg-[Highlight]', 'forced-colors:group-has-[:checked]:forced-color-adjust-none'), children: _jsx("span", { className: "hidden size-3.5 rounded-full bg-surface group-has-[:checked]:block" }) }), _jsx("span", { className: cn('text-[18px] font-medium leading-relaxed text-text', 'group-has-[:checked]:text-[20px] group-has-[:checked]:font-semibold', 'group-has-[:checked]:text-avatar-on-primary-border'), children: children }), _jsx("span", { "aria-hidden": "true", className: cn('ml-auto hidden size-7 shrink-0 items-center justify-center rounded-sm', 'border border-border-strong bg-surface-secondary', 'text-body-sm font-semibold text-text-secondary', 'md:flex group-has-[:focus-visible]:flex'), children: letter })] }));
}
/**
 * Controls whose keys are text, or a search through a list, rather than
 * commands: a letter typed into one of these is the letter, never an answer.
 */
const TEXT_ENTRY = 'textarea, select, [contenteditable]:not([contenteditable="false"]), ' +
    '[role="textbox"], [role="searchbox"], [role="combobox"], [role="listbox"], [role="option"], [role="menu"]';
/** Input types a letter key means nothing to. Every other input takes text. */
const KEYLESS_INPUTS = new Set(['radio', 'checkbox', 'button', 'submit', 'reset', 'range', 'color']);
function expectsText(element) {
    if (element instanceof HTMLInputElement)
        return !KEYLESS_INPUTS.has(element.type);
    return element.closest(TEXT_ENTRY) !== null;
}
/**
 * The letter keys, for the element that holds the question on screen.
 *
 * A letter chooses the option wearing that chip. That is a question-level
 * command — "answer C" — not something the focused option interprets, so it is
 * attached where the question is: the element focus goes to when the question
 * appears. A student can press C the moment they have read the prompt, without
 * first tabbing into the options.
 *
 * It focuses the option and then clicks its radio, so a letter goes down
 * exactly the path a click does: the same `onChange`, the same `onSelect`, the
 * same answer state. Focus lands on the chosen option, so an arrow key pressed
 * next carries on from there.
 *
 * It does nothing at all — the key is left alone — when:
 *
 * - no option in this question wears that letter (D on a true/false);
 * - the key is typed into something that takes text, so "A cab" is typed;
 * - a modifier is held, so Ctrl+C still copies;
 * - the key came from outside the question: a dropdown's list or a dialog is
 *   in a portal, and React still bubbles its keys up to here;
 * - the question is read-only, and its radios are disabled.
 *
 * One question per element: it looks for the letter inside the element it is
 * attached to, and the first match is the one it picks.
 */
export function handleOptionLetterKey(event) {
    if (event.defaultPrevented)
        return;
    if (event.metaKey || event.ctrlKey || event.altKey || event.nativeEvent.isComposing)
        return;
    const scope = event.currentTarget;
    const target = event.target;
    if (!(target instanceof Element) || !scope.contains(target) || expectsText(target))
        return;
    const letter = event.key.toUpperCase();
    if (!/^[A-Z]$/.test(letter))
        return;
    const radio = scope.querySelector(`[data-option-letter="${letter}"] input[type="radio"]`);
    if (!radio || radio.disabled)
        return;
    event.preventDefault();
    radio.focus();
    if (!radio.checked)
        radio.click();
}
/**
 * The fieldset around a question's options.
 *
 * It handles no keys. Arrow keys are the radio group's, and letter keys are
 * the question's — `handleOptionLetterKey`, on the element around this one —
 * because a letter has to work before focus is anywhere near the options.
 */
export function QuestionOptionGroup({ legend, children, className }) {
    return (_jsxs("fieldset", { className: cn('flex flex-col gap-4', className), children: [_jsx("legend", { className: "mb-6 max-w-measure-max text-question text-text text-pretty", children: _jsx(RichText, { children: legend }) }), children] }));
}
//# sourceMappingURL=QuestionOption.js.map